import { AlertTriangle, CheckCircle2 } from "lucide-react";
import React, { useState } from "react";
import { getErrorDetails } from "../../../../app/appUtils";
import AppButton from "../../../../components/ui/AppButton";
import { Modal } from "../../../../components/ui/Modal";
import { useToast } from "../../../../hooks/useToast";
import { DispatchImportReverseResult } from "../../@types";
import { useReverseDispatchImportMutation } from "../../dispatchApiSlice";
import {
  formatImportAmount,
  formatImportDate,
  ImportSection,
  ImportStatsGrid,
} from "./ImportShared";

interface ReverseImportModalProps {
  isOpen: boolean;
  importBatchId: string;
  label?: string;
  onClose: () => void;
  onReversed: (result: DispatchImportReverseResult) => void;
}

const ReverseImportModal: React.FC<ReverseImportModalProps> = ({
  isOpen,
  importBatchId,
  label,
  onClose,
  onReversed,
}) => {
  const toast = useToast();
  const [reverseImport, { isLoading }] = useReverseDispatchImportMutation();
  const [result, setResult] = useState<DispatchImportReverseResult | null>(
    null
  );

  const handleReverse = async () => {
    try {
      const response = await reverseImport(importBatchId).unwrap();
      setResult(response.data);
      onReversed(response.data);
      toast.success({
        title: "Import reversed",
        message:
          response.message ||
          `${response.data?.journeysReversed ?? 0} journey(s) reversed.`,
      });
    } catch (err) {
      toast.error({
        title: "Error",
        message:
          getErrorDetails(err).message ||
          "Failed to reverse import. Please try again.",
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={result ? "Import reversed" : "Reverse import"}
      className="max-w-2xl"
    >
      <div className="p-4 sm:p-6 space-y-5">
        {!result ? (
          <>
            <div className="flex items-start gap-3 border border-red-200 bg-red-50 rounded-lg p-4">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div className="text-[14px] text-red-700 space-y-1">
                <p className="font-semibold">This cannot be undone.</p>
                <p>
                  All journeys and loading sheets created by
                  {label ? ` "${label}"` : " this import"} will be removed, and
                  vehicle and driver totals will be adjusted.
                </p>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <AppButton
                variant="secondary"
                appearance="outline"
                size="md"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </AppButton>
              <AppButton
                variant="danger"
                size="md"
                onClick={handleReverse}
                isLoading={isLoading}
              >
                Reverse import
              </AppButton>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-5 h-5" />
              <p className="text-[14px] font-medium">
                The import batch has been reversed.
              </p>
            </div>

            <ImportStatsGrid
              stats={[
                { label: "Journeys reversed", value: result.journeysReversed },
                {
                  label: "Loading sheets removed",
                  value: result.loadingSheetsRemoved,
                },
                { label: "Vehicles adjusted", value: result.vehiclesAdjusted },
                { label: "Drivers adjusted", value: result.driversAdjusted },
              ]}
            />

            {result.warnings?.length > 0 && (
              <ImportSection title="Warnings" count={result.warnings.length}>
                <ul className="list-disc pl-5 space-y-1 text-[13px] text-yellow-700">
                  {result.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </ImportSection>
            )}

            {result.reversed?.length > 0 && (
              <ImportSection
                title="Reversed journeys"
                count={result.reversed.length}
              >
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                  {result.reversed.map((journey) => (
                    <div
                      key={journey.dispatchId}
                      className="flex flex-wrap items-center justify-between gap-2 p-3 text-[13px]"
                    >
                      <div>
                        <p className="font-medium text-hColor">
                          {journey.dispatchNumber}
                        </p>
                        <p className="text-pColor">
                          {journey.licensePlate} ·{" "}
                          {formatImportDate(journey.tripDate)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-hColor">
                          NGN {formatImportAmount(journey.grossIncome)}
                        </p>
                        {journey.currentAssignmentAdjusted && (
                          <p className="text-[12px] text-pColor">
                            Current assignment adjusted
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ImportSection>
            )}

            <div className="flex justify-end">
              <AppButton size="md" onClick={onClose}>
                Done
              </AppButton>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ReverseImportModal;
